import {useMemo} from 'react'

import {OrderProcessContext} from '../../../context/OrderProcessContext'
import {SlotManagementContext} from '../../../slotManagement/context/SlotManagementContext'
import {useSlotManagementState} from '../../../slotManagement/context/useSlotManagementState'
import {OrderStatus} from '../../types'
import {ChangeableFieldContext} from '../changeableFields/context/ChangeableFieldsContext'
import {
  calculateConfirmationState,
  ChangeableFieldsStateOptions,
  useChangeableFieldsState
} from '../changeableFields/context/useChangeableFieldsState'
import {OrderChangeFeatureToggles} from '../featureToggles'
import {useOrderChangeSlotValidationMutation} from '../slotManagement/hooks'
import {useOrderChangeAvailableTimeSlots} from '../slotManagement/hooks/useOrderChangeAvailableTimeSlots'
import {useOrderChangeSlotValidation} from '../slotManagement/hooks/useOrderChangeSlotValidation'
import {useQuantityDecreaseAutoApprovalRules} from '../slotManagement/rules/useQuantityDecreaseAutoApprovalRules'
import {useSlotChangeableFieldsRules} from '../slotManagement/rules/useSlotManagementChangeableFieldsRules'

import {OrderChangeStateProps, useOrderChangeState} from './useOrderChangeState'

/** Default refresh interval for time-based changeable field state transitions (60 seconds) */
const REFRESH_INTERVAL = 60_000

const SLOT_MANAGEMENT_HOOKS = {
  useAvailableTimeSlots: useOrderChangeAvailableTimeSlots,
  useSlotValidation: useOrderChangeSlotValidation,
  useSlotValidationMutation: useOrderChangeSlotValidationMutation
}

/**
 * Props for OrderChangeContextProvider component.
 * Combines order change state, changeable fields configuration, and feature toggles.
 */
type OrderChangeContextProviderProps = React.PropsWithChildren &
  Omit<OrderChangeStateProps, 'changeableFields'> &
  ChangeableFieldsStateOptions & {
    /** Feature toggles for order change functionality */
    featureToggles: OrderChangeFeatureToggles
  }

/**
 * Provides order change state to its children via React context.
 *
 * This provider orchestrates multiple context layers:
 * 1. **Base changeable fields logic** - Time-based field editability rules
 * 2. **Slot management layer** - Promotes fields to auto-confirmable when slots are available
 * 3. **Order change state** - Overall order change metadata and validation
 *
 * **Layered architecture:**
 * ```
 * Base Changeable Fields (time-based rules)
 *         ↓
 * Slot Management Rules (optional promotion to auto-confirmable)
 *         ↓
 * Final Changeable Fields State
 * ```
 *
 * **Auto-refresh behavior:**
 * - Updates every 60 seconds (configurable) for time-based field state transitions
 * - Immediately responds to order data changes (order.orderManagement.isChangeable)
 * - Slot availability changes are managed separately via SlotManagementContext
 *
 * **Slot management behavior:**
 * - When enabled AND slot is available: eligible fields become auto-confirmable
 * - When disabled OR no slot: standard time-based rules apply
 * - Only affects specific fields that support slot-based approval (dateTime, quantity, etc.)
 * - Does not downgrade fields - only promotes requireDispatchConfirmation → autoConfirmable
 *
 * @param props - Provider configuration
 * @param props.order - Order with orderManagement.isChangeable property
 * @param props.changeableFieldsOptions - Field timing constraints from API
 * @param props.refreshIntervalMs - Interval for time-based state refresh (default: 60s)
 * @param props.featureToggles - Feature toggles including slot management enablement
 * @param props.children - Child components that will consume these contexts
 *
 * @example
 * ```tsx
 * <OrderChangeContextProvider
 *   order={order}
 *   changeableFieldsOptions={options.changeableFields}
 *   featureToggles={featureToggles}
 * >
 *   <OrderChangeForm />
 * </OrderChangeContextProvider>
 * ```
 */
export const OrderChangeContextProvider = ({
  children,
  changeableFieldsOptions,
  refreshIntervalMs = REFRESH_INTERVAL,
  order,
  featureToggles
}: OrderChangeContextProviderProps) => {
  // Step 1: Calculate base changeable field states using time-based rules
  // This provides the foundation for field editability based on deadlines
  const changeableFieldsState = useChangeableFieldsState({
    changeableFieldsOptions,
    refreshIntervalMs
  })

  // Step 2: Initialize slot management state (tracks slot availability for form dateTimes)
  // Synchronizes with form's dateTime field to maintain consistent slot tracking
  const slotManagementConfig = useMemo(() => {
    const schedule = order.schedules[0]

    return {
      isFeatureEnabled: featureToggles.enableSlotManagementForRmcOrderManagement,
      orgUnitId: order.orgUnitId,
      siteNumber: order.shippingAddress.siteNumber,
      orderId: order.orderId,
      // Fallbacks for when the options API returns an empty contracts list
      contractNumber: order.contractNumber ?? undefined,
      contractItemPositionNumber: order.contractItem ?? undefined,
      // Used to skip slot validation when quantity is decreased on confirmed orders
      isOrderConfirmedByCustomer: order.orderStatus === OrderStatus.Confirmed,
      // Must stay in sync with fields/dateTime/buildDateTimeForm: the variation-code resolver only
      // applies the adjusted slot reference while the form still represents this original instant.
      originalDateTimeForSlotReference:
        schedule?.scheduleStartDate ?? order.shippingDate ?? schedule?.scheduleEarliestStartDate,
      variationCode: order.variationCode,
      variationCodeOffset: order.variationCodeOffset,
      variationCodeAdjustedTime: order.variationCodeAdjustedTime
    }
  }, [featureToggles.enableSlotManagementForRmcOrderManagement, order])

  const slotManagementState = useSlotManagementState(slotManagementConfig, SLOT_MANAGEMENT_HOOKS)

  // Step 3: Apply slot management rules to promote eligible fields
  // Only affects fields when:
  // - Slot management is enabled
  // - A slot is available for the dateTime
  // - Field state is requireDispatchConfirmation (can be promoted)
  // Apply to orderChangeRulesOnly (before slot rules) to get clean intermediate state
  const changeableFieldsWithSlotManagement = useSlotChangeableFieldsRules(
    changeableFieldsState.orderChangeRulesOnlyChangeableFields,
    {
      isSlotManagementEnabled: featureToggles.enableSlotManagementForRmcOrderManagement,
      // For order management, we only have one dateTime (single delivery)
      // Extract the first slot's availability status
      isSlotAvailable: slotManagementState.slotAvailabilities[0]?.isAvailable ?? false
    }
  )

  // Step 3b: Apply quantity decrease auto-approval rule
  // When quantity is decreased on a confirmed order, promote quantity field to
  // auto-confirmable since slot validation is skipped for this case.
  const changeableFieldsWithQuantityDecreaseRule = useQuantityDecreaseAutoApprovalRules(
    changeableFieldsWithSlotManagement,
    {isOrderConfirmedByCustomer: order.orderStatus === OrderStatus.Confirmed}
  )

  // Step 4: Calculate overall order change state (metadata, validation)
  // Uses the base changeable fields (without slot management) for consistency
  const orderChangeState = useOrderChangeState({
    order,
    changeableFields: changeableFieldsState.changeableFields
  })

  // Step 5: Combine slot management results with confirmation state
  // Provide all three levels: base, orderChangeRulesOnly, and final (with slot management)
  const changeableFieldsStateWithSlotManagement = useMemo(
    () => ({
      // Level 1: Base (configuration + time-based rules only)
      baseChangeableFields: changeableFieldsState.baseChangeableFields,
      // Level 2: Order Change rules applied (spacing, material, order confirmation)
      orderChangeRulesOnlyChangeableFields:
        changeableFieldsState.orderChangeRulesOnlyChangeableFields,
      // Level 3: Final with slot management + quantity decrease rules
      changeableFields: changeableFieldsWithQuantityDecreaseRule,
      confirmationState: calculateConfirmationState(changeableFieldsWithQuantityDecreaseRule)
    }),
    [changeableFieldsState, changeableFieldsWithQuantityDecreaseRule]
  )

  return (
    <SlotManagementContext.Provider value={slotManagementState}>
      <ChangeableFieldContext.Provider value={changeableFieldsStateWithSlotManagement}>
        <OrderProcessContext.Provider value={orderChangeState}>
          {children}
        </OrderProcessContext.Provider>
      </ChangeableFieldContext.Provider>
    </SlotManagementContext.Provider>
  )
}
